import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { formatVND } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Download, Printer, ChevronLeft, ChevronRight,
  ChevronsLeft, ChevronsRight, RefreshCw, Plus, Calendar,
  List, LayoutGrid, ChevronUp, ExternalLink,
} from "lucide-react";
import { MobileFilterToggle, MobileFilterClose, sidebarClasses } from "@/components/PageWithSidebar";

const FUND_TABS = [
  { key: "tien_mat", label: "Tiền mặt" },
  { key: "ngan_hang", label: "Ngân hàng" },
  { key: "vi_dien_tu", label: "Ví điện tử" },
  { key: "tong_quy", label: "Tổng quỹ" },
];

const STATUS_OPTIONS = [
  { key: "paid", label: "Đã thanh toán" },
  { key: "cancelled", label: "Đã hủy" },
];

const TIME_OPTIONS = [
  { key: "today", label: "Hôm nay" },
  { key: "this_week", label: "Tuần này" },
  { key: "this_month", label: "Tháng này" },
  { key: "30days", label: "30 ngày qua" },
  { key: "90days", label: "90 ngày qua" },
  { key: "365days", label: "1 năm qua" },
  { key: "all", label: "Tất cả thời gian" },
  { key: "custom", label: "Lựa chọn khác" },
];

const CATEGORY_OPTIONS = [
  "Thu Tiền khách trả",
  "Thu hoàn tiền nhà cung cấp",
  "Thu từ bán hàng",
  "Thu khác",
  "Chi trả nhà cung cấp",
  "Chi lương nhân viên",
  "Chi phí mặt bằng",
  "Chi phí vận hành",
  "Chi khác",
];

function formatDateTime(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) {
      if (dateStr.includes("T")) {
        const p = dateStr.split("T"), dp = p[0].split("-"), tp = (p[1]||"").split(":").slice(0,2).join(":");
        return `${dp[2]}/${dp[1]}/${dp[0]} ${tp}`;
      }
      return dateStr;
    }
    return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
  } catch { return dateStr; }
}

export default function CashBookPage() {
  const [fundTab, setFundTab] = useState("tien_mat");
  const [searchCode, setSearchCode] = useState("");
  const [searchNote, setSearchNote] = useState("");
  const [timeFilter, setTimeFilter] = useState("30days");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilters, setStatusFilters] = useState(["paid"]);
  const [creatorFilter, setCreatorFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [showFilter, setShowFilter] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [createDialog, setCreateDialog] = useState(null); // "thu" | "chi" | null

  const queryParams = useMemo(() => ({
    search: searchCode || undefined,
    search_note: searchNote || undefined,
    time_filter: timeFilter,
    date_from: timeFilter === "custom" ? dateFrom : undefined,
    date_to: timeFilter === "custom" ? dateTo : undefined,
    status: statusFilters.length > 0 ? statusFilters.join(",") : undefined,
    payment_method: fundTab !== "tong_quy" ? fundTab : undefined,
    creator: creatorFilter || undefined,
    page, page_size: pageSize,
  }), [searchCode, searchNote, timeFilter, dateFrom, dateTo, statusFilters, fundTab, creatorFilter, page, pageSize]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["cashbook", queryParams],
    queryFn: () => api.get("/cashbook", { params: queryParams }).then(r => r.data),
    keepPreviousData: true,
  });

  const receipts = data?.receipts || [];
  const totalCount = data?.total || 0;
  const summary = data?.summary || {};
  const totalPages = Math.ceil(totalCount / pageSize);

  const toggleStatus = (key) => { setStatusFilters(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]); setPage(1); };

  const handleSync = async () => {
    setSyncing(true);
    try {
      // Try KiotViet sync first
      const res = await api.post("/cashbook/sync", null, { params: { days: 30 } });
      // Then auto-generate from invoices
      const res2 = await api.post("/cashbook/generate-from-invoices", null, { params: { days: 90 } });
      const msg = [res.data.message, res2.data.count > 0 ? res2.data.message : ""].filter(Boolean).join(". ");
      toast.success(msg || "Đồng bộ thành công");
      refetch();
    } catch (err) { toast.error("Lỗi: " + (err.response?.data?.detail || err.message)); }
    finally { setSyncing(false); }
  };

  const handleExport = () => {
    const headers = ["Mã phiếu","Thời gian","Loại thu chi","Người nộp/nhận","Giá trị","Trạng thái"];
    const rows = receipts.map(r => [r.code, formatDateTime(r.created_date), r.category, r.payer_name, r.amount, r.status_label]);
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `so_quy_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url); toast.success("Đã xuất file CSV");
  };

  const handlePrint = (item) => {
    const typeLabel = item.receipt_type === "thu" ? "PHIẾU THU" : "PHIẾU CHI";
    const win = window.open("", "_blank");
    win.document.write(`<html><head><title>${typeLabel} ${item.code}</title>
      <style>body{font-family:sans-serif;padding:20px;max-width:500px;margin:0 auto}.center{text-align:center}p{margin:5px 0}.bold{font-weight:bold}</style></head>
      <body><h2 class="center">KARINOX Arena</h2><h3 class="center">${typeLabel}</h3><hr/>
      <p><b>Mã phiếu:</b> ${item.code}</p>
      <p><b>Ngày:</b> ${formatDateTime(item.created_date)}</p>
      <p><b>Loại:</b> ${item.category}</p>
      <p><b>Giá trị:</b> ${formatVND(item.amount)}</p>
      <p><b>Người nộp/nhận:</b> ${item.payer_name||"-"}</p>
      <p><b>Người tạo:</b> ${item.creator_name}</p>
      <p><b>Ghi chú:</b> ${item.note||"-"}</p>
      <hr/><p class="center">Chi nhánh: ${item.branch_name}</p></body></html>`);
    win.document.close(); win.print();
  };

  return (
    <div className="flex h-full bg-white dark:bg-slate-900" data-testid="cashbook-page">
      {/* Left Sidebar */}
      <aside className={sidebarClasses(showFilter)}>
        <MobileFilterClose setShowFilter={setShowFilter} />
        {/* Fund Type Radio */}
        <div>
          <div className="space-y-2">
            {FUND_TABS.map(tab => (
              <label key={tab.key} className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="fund" checked={fundTab === tab.key} onChange={() => { setFundTab(tab.key); setPage(1); }} className="accent-blue-600" />
                <span className="text-sm text-gray-700 dark:text-slate-300">{tab.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Search */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center justify-between">Tìm kiếm<ChevronUp className="w-4 h-4" /></h4>
          <Input placeholder="Theo mã phiếu" value={searchCode} onChange={e=>{setSearchCode(e.target.value);setPage(1);}} className="mb-2 text-sm" data-testid="search-cb-code" />
          <Input placeholder="Ghi chú" value={searchNote} onChange={e=>{setSearchNote(e.target.value);setPage(1);}} className="mb-2 text-sm" data-testid="search-cb-note" />
        </div>

        {/* Status */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center justify-between">Trạng thái<ChevronUp className="w-4 h-4" /></h4>
          <div className="space-y-1.5">
            {STATUS_OPTIONS.map(opt => (
              <label key={opt.key} className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={statusFilters.includes(opt.key)} onCheckedChange={() => toggleStatus(opt.key)} data-testid={`cb-status-${opt.key}`} />
                <span className="text-sm text-gray-600 dark:text-slate-400">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Time */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center justify-between">Thời gian<ChevronUp className="w-4 h-4" /></h4>
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="time" checked={timeFilter!=="custom"} onChange={()=>setTimeFilter("30days")} className="accent-blue-600" />
              <select value={timeFilter==="custom"?"30days":timeFilter} onChange={e=>{setTimeFilter(e.target.value);setPage(1);}} className="text-sm border border-gray-300 dark:border-slate-600 rounded px-2 py-1 bg-white text-gray-900 dark:bg-slate-700 dark:text-slate-200 flex-1" data-testid="cb-time-filter">
                {TIME_OPTIONS.filter(t=>t.key!=="custom").map(t=>(<option key={t.key} value={t.key} className="bg-white text-gray-900 dark:bg-slate-700 dark:text-slate-200">{t.label}</option>))}
              </select>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="time" checked={timeFilter==="custom"} onChange={()=>setTimeFilter("custom")} className="accent-blue-600" />
              <span className="text-sm text-gray-600 dark:text-slate-400">Lựa chọn khác</span><Calendar className="w-4 h-4 text-gray-400" />
            </label>
            {timeFilter==="custom"&&(<div className="pl-6 space-y-1"><Input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} className="text-xs" /><Input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} className="text-xs" /></div>)}
          </div>
        </div>

        {/* Creator */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center justify-between">Người tạo<ChevronUp className="w-4 h-4" /></h4>
          <Input placeholder="Chọn người tạo" value={creatorFilter} onChange={e=>{setCreatorFilter(e.target.value);setPage(1);}} className="text-sm" data-testid="cb-creator-filter" />
        </div>

        {/* Page Size */}
        <div className="pt-2 border-t border-gray-200 dark:border-slate-700">
          <label className="text-sm text-gray-600 dark:text-slate-400 flex items-center gap-2">Số bản ghi:
            <select value={pageSize} onChange={e=>{setPageSize(Number(e.target.value));setPage(1);}} className="border border-gray-300 dark:border-slate-600 rounded px-2 py-1 bg-white text-gray-900 dark:bg-slate-700 dark:text-slate-200 text-sm">
              <option value={10}>10</option><option value={20}>20</option><option value={50}>50</option><option value={100}>100</option>
            </select>
          </label>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 sm:px-6 py-3 sm:py-4 gap-2 border-b border-gray-200 dark:border-slate-700">
          <h1 className="text-xl font-bold text-gray-800 dark:text-slate-100" data-testid="cashbook-title">
            Sổ quỹ {fundTab === "tien_mat" ? "tiền mặt" : fundTab === "ngan_hang" ? "ngân hàng" : fundTab === "vi_dien_tu" ? "ví điện tử" : ""}
          </h1>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={()=>setCreateDialog("thu")} className="bg-green-600 hover:bg-green-700 text-white text-sm gap-1" data-testid="btn-create-thu"><Plus className="w-4 h-4" /> Lập phiếu thu</Button>
            <Button onClick={()=>setCreateDialog("chi")} className="bg-green-600 hover:bg-green-700 text-white text-sm gap-1" data-testid="btn-create-chi"><Plus className="w-4 h-4" /> Lập phiếu chi</Button>
            <Button onClick={handleSync} variant="outline" className="text-sm gap-1" disabled={syncing} data-testid="btn-sync-cb"><RefreshCw className={`w-4 h-4 ${syncing?"animate-spin":""}`} />{syncing?"Đang đồng bộ...":"Đồng bộ"}</Button>
            <Button onClick={handleExport} className="bg-green-600 hover:bg-green-700 text-white text-sm gap-1" data-testid="btn-export-cb"><Download className="w-4 h-4" /> Xuất file</Button>
            <div className="flex border border-gray-300 dark:border-slate-600 rounded overflow-hidden">
              <button className="p-1.5 bg-blue-600 text-white"><List className="w-4 h-4" /></button>
              <button className="p-1.5 bg-white dark:bg-slate-700 text-gray-600 dark:text-slate-300"><LayoutGrid className="w-4 h-4" /></button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-0 border-b border-gray-200 dark:border-slate-700">
          <div className="px-4 py-3 text-center border-r border-gray-200 dark:border-slate-700">
            <p className="text-xs text-gray-500 dark:text-slate-400">Quỹ đầu kỳ</p>
            <p className="text-lg font-bold text-blue-600 dark:text-blue-400" data-testid="cb-quy-dau-ky">{formatVND(summary.quy_dau_ky || 0)}</p>
          </div>
          <div className="px-4 py-3 text-center border-r border-gray-200 dark:border-slate-700">
            <p className="text-xs text-gray-500 dark:text-slate-400">Tổng thu</p>
            <p className="text-lg font-bold text-green-600 dark:text-green-400" data-testid="cb-tong-thu">{formatVND(summary.total_thu || 0)}</p>
          </div>
          <div className="px-4 py-3 text-center border-r border-gray-200 dark:border-slate-700">
            <p className="text-xs text-gray-500 dark:text-slate-400">Tổng chi</p>
            <p className="text-lg font-bold text-red-600 dark:text-red-400" data-testid="cb-tong-chi">{formatVND(summary.total_chi || 0)}</p>
          </div>
          <div className="px-4 py-3 text-center">
            <p className="text-xs text-gray-500 dark:text-slate-400">Tồn quỹ</p>
            <p className="text-lg font-bold text-blue-700 dark:text-blue-300" data-testid="cb-ton-quy">{formatVND(summary.ton_quy || 0)}</p>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm" data-testid="cashbook-table">
            <thead className="bg-gray-50 dark:bg-slate-800 sticky top-0 z-10">
              <tr className="border-b border-gray-200 dark:border-slate-700">
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-slate-300">Mã phiếu</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-slate-300">Thời gian</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-slate-300">Loại thu chi</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-slate-300">Người nộp/nhận</th>
                <th className="px-3 py-2.5 text-right font-semibold text-gray-700 dark:text-slate-300">Giá trị</th>
              </tr>
            </thead>
            <tbody>
              {isLoading?(<tr><td colSpan={5} className="text-center py-10 text-gray-400">Đang tải...</td></tr>):receipts.length===0?(<tr><td colSpan={5} className="text-center py-10 text-gray-400">Không có phiếu thu/chi nào</td></tr>):(
                receipts.map(item=>(
                  <React.Fragment key={item.id}>
                    <tr className={`border-b border-gray-100 dark:border-slate-700 cursor-pointer transition-colors ${expandedRow===item.id?"bg-blue-50 dark:bg-blue-900/30 font-medium":"hover:bg-gray-50 dark:hover:bg-slate-800/50"}`} onClick={()=>setExpandedRow(expandedRow===item.id?null:item.id)} data-testid={`cb-row-${item.code}`}>
                      <td className="px-3 py-2.5 text-gray-800 dark:text-slate-200">{item.code}</td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-slate-400">{formatDateTime(item.created_date)}</td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-slate-400">{item.category}</td>
                      <td className="px-3 py-2.5 text-gray-600 dark:text-slate-400">{item.payer_name}</td>
                      <td className={`px-3 py-2.5 text-right font-medium ${item.receipt_type==="thu"?"text-green-600 dark:text-green-400":"text-red-600 dark:text-red-400"}`}>{item.receipt_type==="chi"?"-":""}{formatVND(item.amount)}</td>
                    </tr>
                    {expandedRow===item.id&&(
                      <tr className="bg-blue-50/30 dark:bg-blue-900/10"><td colSpan={5} className="p-0">
                        <ReceiptDetail item={item} onPrint={()=>handlePrint(item)} />
                      </td></tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between px-4 sm:px-6 py-2 sm:py-3 gap-2 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          <div className="flex items-center gap-1">
            <button onClick={()=>setPage(1)} disabled={page<=1} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"><ChevronsLeft className="w-4 h-4" /></button>
            <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page<=1} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
            {Array.from({length:Math.min(5,totalPages)},(_,i)=>{let pn;if(totalPages<=5)pn=i+1;else if(page<=3)pn=i+1;else if(page>=totalPages-2)pn=totalPages-4+i;else pn=page-2+i;return(<button key={pn} onClick={()=>setPage(pn)} className={`w-8 h-8 rounded text-sm ${page===pn?"bg-blue-600 text-white":"hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-600 dark:text-slate-400"}`}>{pn}</button>);})}
            <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page>=totalPages} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
            <button onClick={()=>setPage(totalPages)} disabled={page>=totalPages} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"><ChevronsRight className="w-4 h-4" /></button>
          </div>
          <span className="text-sm text-gray-500 dark:text-slate-400" data-testid="cb-pagination-info">Hiển thị {totalCount>0?(page-1)*pageSize+1:0} - {Math.min(page*pageSize,totalCount)} trên tổng số {totalCount} phiếu</span>
        </div>
      </main>

      <MobileFilterToggle showFilter={showFilter} setShowFilter={setShowFilter} />

      {/* Create Receipt Dialog */}
      <CreateReceiptDialog open={!!createDialog} type={createDialog} onClose={()=>setCreateDialog(null)} fundTab={fundTab} onSuccess={()=>{setCreateDialog(null);refetch();}} />
    </div>
  );
}

function ReceiptDetail({ item, onPrint }) {
  return (
    <div className="px-6 py-4 border-t border-blue-100 dark:border-blue-800" data-testid="cb-detail-panel">
      <div className="flex gap-4 border-b border-gray-200 dark:border-slate-700 mb-4">
        <button className="pb-2 px-1 text-sm font-medium border-b-2 border-blue-600 text-blue-600">Thông tin</button>
      </div>
      <div className="grid grid-cols-2 gap-x-12 gap-y-3 mb-4">
        <div className="flex items-center gap-3"><span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Mã phiếu:</span><span className="text-sm font-medium text-blue-600 dark:text-blue-400">{item.code}</span></div>
        <div className="flex items-center gap-3"><span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Chi nhánh:</span><span className="text-sm text-gray-700 dark:text-slate-300">{item.branch_name}</span></div>
        <div className="flex items-center gap-3"><span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Thời gian:</span><span className="text-sm text-gray-700 dark:text-slate-300">{formatDateTime(item.created_date)}</span></div>
        <div className="flex items-center gap-3"><span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Trạng thái:</span><span className={`text-sm font-medium ${item.status==="paid"?"text-green-600":"text-red-600"}`}>{item.status_label}</span></div>
        <div className="flex items-center gap-3"><span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Giá trị:</span><span className="text-sm font-medium text-gray-800 dark:text-slate-200">{formatVND(item.amount)}</span></div>
        <div className="flex items-center gap-3"><span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Người tạo:</span><span className="text-sm text-gray-700 dark:text-slate-300">{item.creator_name}</span></div>
        <div className="flex items-center gap-3"><span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Loại thu chi:</span><span className="text-sm text-gray-700 dark:text-slate-300">{item.category}</span></div>
        <div className="flex items-center gap-3"><span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Nhân viên:</span><span className="text-sm text-gray-700 dark:text-slate-300">{item.staff_name||"-"}</span></div>
        <div className="flex items-center gap-3"><span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Tên người nộp:</span><span className="text-sm text-gray-700 dark:text-slate-300">{item.payer_name||"-"}</span></div>
        <div className="flex items-center gap-3"><span className="text-sm text-gray-500 dark:text-slate-400 w-32 shrink-0">Nhóm người nộp:</span><span className="text-sm text-gray-700 dark:text-slate-300">{item.payer_group||"-"}</span></div>
      </div>
      {item.linked_invoice_code && (
        <p className="text-sm text-gray-500 dark:text-slate-400 italic mb-4">
          Phiếu thu tự động được tạo gắn với hóa đơn <span className="text-blue-600 dark:text-blue-400 font-medium">{item.linked_invoice_code}</span>
        </p>
      )}
      {item.note && <p className="text-sm text-gray-500 dark:text-slate-400 italic mb-4">Ghi chú: {item.note}</p>}
      <div className="flex justify-end gap-2 pt-3 border-t border-gray-200 dark:border-slate-700">
        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs gap-1" data-testid="btn-open-receipt"><ExternalLink className="w-3 h-3" /> Mở phiếu</Button>
        <Button size="sm" variant="outline" className="text-xs gap-1" onClick={onPrint} data-testid="btn-print-receipt"><Printer className="w-3 h-3" /> In</Button>
      </div>
    </div>
  );
}

function CreateReceiptDialog({ open, type, onClose, fundTab, onSuccess }) {
  const [category, setCategory] = useState("");
  const [amount, setAmount] = useState("");
  const [payerName, setPayerName] = useState("");
  const [payerGroup, setPayerGroup] = useState("Khách hàng");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const categories = type === "thu"
    ? CATEGORY_OPTIONS.filter(c => c.startsWith("Thu"))
    : CATEGORY_OPTIONS.filter(c => c.startsWith("Chi"));

  const handleSubmit = async () => {
    if (!amount || Number(amount) <= 0) { toast.error("Vui lòng nhập giá trị"); return; }
    setSubmitting(true);
    try {
      const res = await api.post("/cashbook", {
        receipt_type: type,
        category: category || categories[0] || "",
        amount: Number(amount),
        payer_name: payerName,
        payer_group: payerGroup,
        payment_method: fundTab !== "tong_quy" ? fundTab : "tien_mat",
        note,
      });
      toast.success(res.data.message);
      setCategory(""); setAmount(""); setPayerName(""); setNote("");
      onSuccess();
    } catch (err) { toast.error("Lỗi: " + (err.response?.data?.detail || err.message)); }
    finally { setSubmitting(false); }
  };

  const typeLabel = type === "thu" ? "phiếu thu" : "phiếu chi";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>Lập {typeLabel}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-1 block">Loại {type === "thu" ? "thu" : "chi"}</label>
            <select value={category} onChange={e=>setCategory(e.target.value)} className="w-full text-sm border border-gray-300 dark:border-slate-600 rounded px-3 py-2 bg-white text-gray-900 dark:bg-slate-700 dark:text-slate-200" data-testid="create-cb-category">
              <option value="">-- Chọn loại --</option>
              {categories.map(c => <option key={c} value={c} className="bg-white text-gray-900 dark:bg-slate-700 dark:text-slate-200">{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-1 block">Giá trị</label>
            <Input type="number" placeholder="Nhập số tiền" value={amount} onChange={e=>setAmount(e.target.value)} data-testid="create-cb-amount" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-1 block">Người {type==="thu"?"nộp":"nhận"}</label>
              <Input placeholder="Tên..." value={payerName} onChange={e=>setPayerName(e.target.value)} data-testid="create-cb-payer" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-1 block">Nhóm</label>
              <select value={payerGroup} onChange={e=>setPayerGroup(e.target.value)} className="w-full text-sm border border-gray-300 dark:border-slate-600 rounded px-3 py-2 bg-white text-gray-900 dark:bg-slate-700 dark:text-slate-200">
                <option value="Khách hàng">Khách hàng</option>
                <option value="Nhà cung cấp">Nhà cung cấp</option>
                <option value="Nhân viên">Nhân viên</option>
                <option value="Khác">Khác</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-1 block">Ghi chú</label>
            <Input placeholder="Ghi chú..." value={note} onChange={e=>setNote(e.target.value)} data-testid="create-cb-note" />
          </div>
        </div>
        <DialogFooter className="gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Đóng</Button>
          <Button onClick={handleSubmit} disabled={submitting} className="bg-green-600 hover:bg-green-700 text-white" data-testid="submit-create-cb">{submitting?"Đang tạo...":`Lập ${typeLabel}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
